import React, { useEffect, useState, useCallback } from 'react';
import { CloseableModal } from '../../../../../../../../components/CloseableModal';
import { ModalBody } from './ModalBody';
import { TriggerService } from '../../../../../../../../services/applicant_tracking/TriggerService';

const DEFAULT_PER_PAGE = 15;

interface PropTypes {
  triggerId: number;
  isOpen: boolean;
  onCancel: () => void;
  onContinue: () => void;
}

export function DeleteTriggerConfirmationModal(props: PropTypes) {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [candidateEmails, setCandidateEmails] = useState<string[]>([]);
  const [entriesCount, setEntriesCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loadExecutions = useCallback(
    async (page: number, searchTerm: string, append: boolean) => {
      const data = await TriggerService.getPendingExecutions(props.triggerId, {
        perPage: DEFAULT_PER_PAGE,
        offset: page,
        search: searchTerm || undefined,
      });

      if (append) {
        setCandidateEmails((prev) => [...prev, ...data.candidateEmails]);
      } else {
        setCandidateEmails(data.candidateEmails);
      }

      setEntriesCount(data.entriesCount);
      setNextPage(data.nextPage);

      return data;
    },
    [props.triggerId],
  );

  useEffect(() => {
    if (props.isOpen) {
      setIsLoading(true);
      setError(null);
      setSearch('');

      loadExecutions(1, '', false)
        .then((data) => {
          if (data.entriesCount === 0) {
            props.onContinue();
          } else {
            setTotalCount(data.entriesCount);
          }
        })
        .catch(() => {
          setError('Failed to load pending executions. Please try again.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [props.isOpen, props.triggerId]);

  const handleLoadMore = useCallback(async () => {
    if (nextPage == null) return;

    setIsLoadingMore(true);
    try {
      await loadExecutions(nextPage, search, true);
    } catch {
      setError('Failed to load more executions. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextPage, search, loadExecutions]);

  const handleSearchChange = useCallback(
    async (value: string) => {
      setSearch(value);
      setIsLoading(true);
      setError(null);

      try {
        await loadExecutions(1, value, false);
      } catch {
        setError('Failed to search executions. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [loadExecutions],
  );

  return (
    <CloseableModal
      className='modal-dialog-centered'
      onClose={props.onCancel}
      isOpen={props.isOpen}
      size='md'
      closeDisabled={false}
      keyboard={true}
      backdrop={true}
      headerTitle='Remove Automation'
      bodyChildren={
        <ModalBody
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          error={error}
          candidateEmails={candidateEmails}
          entriesCount={entriesCount}
          totalCount={totalCount}
          hasMore={nextPage != null}
          search={search}
          onSearchChange={handleSearchChange}
          onLoadMore={handleLoadMore}
          onCancel={props.onCancel}
          onContinue={props.onContinue}
        />
      }
    />
  );
}
